import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, ShieldCheck, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { TruncatedText } from "@/components/ui/truncated-text";
import { useAuth } from "@/hooks/useAuth";
import { notifyDefectClosed, notifyDefectStatusChanged } from "@/lib/notifications";

/**
 * TES-548: the verify/reject ACTION is server-enforced via the SECURITY INVOKER
 * RPCs verify_defect / reject_defect_verification (Migration 296). The RPCs
 * enforce four-eyes (caller <> assigned_to), the mandatory comment, valid
 * resolved -> closed / resolved -> in_progress transitions and write the comment
 * to defect_comments — the UI cannot bypass them. Typed via a narrow cast until
 * types.ts is regenerated for the new functions.
 *
 * TES-2229/TES-2037-1 (LH §L5.10, Mig 399): weiches Maßnahmen-Gate — offene
 * Maßnahmen (defect_action) / offene Kind-Mängel verlangen serverseitig eine
 * explizite Override-Begründung (_override_open_actions_reason, auditiert);
 * PT-TSK-Links sind advisorisch und blockieren nie.
 */
type DefectVerifyRpc = {
  rpc: (
    fn: "verify_defect" | "reject_defect_verification",
    args: { _defect_id: string; _comment: string; _override_open_actions_reason?: string | null },
  ) => Promise<{ data: unknown; error: { code?: string; message: string } | null }>;
};

/** Offene Maßnahmen + offene Kind-Mängel (weiches Gate) — advisory UI-Zähler. */
async function fetchOpenGateCounts(
  defectId: string,
): Promise<{ openActions: number; openChildren: number }> {
  const [actions, children] = await Promise.all([
    supabase
      .from("defect_action")
      .select("id", { count: "exact", head: true })
      .eq("defect_id", defectId)
      .neq("status", "done"),
    supabase
      .from("defects")
      .select("id", { count: "exact", head: true })
      .eq("parent_defect_id", defectId)
      .is("deleted_at", null)
      .not("status", "in", "(resolved,closed,cancelled)"),
  ]);
  return { openActions: actions.count ?? 0, openChildren: children.count ?? 0 };
}

interface VerifyDefectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defect: {
    id: string;
    title: string;
    assigned_to?: string | null;
    created_by?: string | null;
  } | null;
  onVerified?: () => void;
}

export function VerifyDefectDialog({
  open,
  onOpenChange,
  defect,
  onVerified,
}: VerifyDefectDialogProps) {
  const { t } = useTranslation();

  const { user, profile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"verify" | "reject">("verify");
  const [verificationNotes, setVerificationNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  // TES-2037-1: advisory Zähler für das weiche Gate (Warnung „n offene Maßnahmen").
  const { data: gate } = useQuery({
    queryKey: ["defect-open-gate", defect?.id],
    queryFn: () => fetchOpenGateCounts(defect!.id),
    enabled: open && !!defect?.id && !!user,
    staleTime: 15_000,
  });
  const openGateCount = (gate?.openActions ?? 0) + (gate?.openChildren ?? 0);

  const handleVerify = async () => {
    if (!defect || !user) return;

    // TES-548 (SYS-13): the verification comment is MANDATORY. The server RPC
    // also rejects an empty comment (P0422); we block early to save a round-trip.
    if (!verificationNotes.trim()) {
      toast.error(t("defects.verifyDialog.verificationCommentRequired"), {
        description: t("defects.verifyDialog.verificationCommentRequiredDesc"),
      });
      return;
    }

    // TES-2037-1 (LH §L5.10): offene Maßnahmen/Kind-Mängel → QM darf mit
    // expliziter Begründung vorgreifen (auditiert). Der Server erzwingt das
    // ebenfalls (P0422 defect_open_actions_gate); early-block spart den Roundtrip.
    if (openGateCount > 0 && !overrideReason.trim()) {
      toast.error(t("defects.verifyDialog.openActionsOverrideRequired"), {
        description: t("defects.verifyDialog.openActionsOverrideRequiredDesc", {
          count: openGateCount,
        }),
      });
      return;
    }

    setSaving(true);
    try {
      // TES-548: resolved -> closed via verify_defect (SECURITY INVOKER,
      // RLS-enforced). Four-eyes (caller <> assigned_to), the mandatory comment
      // and the transition are enforced server-side; attribution (verifying_qm
      // := acting QM, TES-1141) is set inside the RPC.
      const { error } = await (supabase as unknown as DefectVerifyRpc).rpc("verify_defect", {
        _defect_id: defect.id,
        _comment: verificationNotes.trim(),
        _override_open_actions_reason: openGateCount > 0 ? overrideReason.trim() : null,
      });

      if (error) throw error;

      // Send notifications to assignee and creator
      const closedByName =
        profile?.display_name || profile?.first_name || profile?.email?.split("@")[0];

      // Notify assignee that defect was closed
      if (defect.assigned_to) {
        notifyDefectClosed(defect.assigned_to, defect.title, defect.id, closedByName);
      }

      // Notify creator if different from assignee
      if (defect.created_by && defect.created_by !== defect.assigned_to) {
        notifyDefectClosed(defect.created_by, defect.title, defect.id, closedByName);
      }

      toast.success(t("defects.verifyDialog.verifiedTitle"), {
        description: t("defects.verifyDialog.verifiedDescription"),
      });

      resetForm();
      onOpenChange(false);
      onVerified?.();
    } catch (error) {
      console.error("Error verifying defect:", error);
      toast.error(t("common.error"), {
        description: t("defects.verifyDialog.errorDescription"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!defect) return;

    if (!rejectionReason.trim()) {
      toast.error(t("defects.verifyDialog.rejectionReasonRequired"), {
        description: t("defects.verifyDialog.rejectionReasonRequiredDesc"),
      });
      return;
    }

    setSaving(true);
    try {
      // TES-548 (SYS-14): resolved -> in_progress via reject_defect_verification
      // (SECURITY INVOKER, RLS-enforced). The mandatory reason comment is stored
      // in defect_comments and mirrored into resolution_notes by the RPC.
      const { error } = await (supabase as unknown as DefectVerifyRpc).rpc(
        "reject_defect_verification",
        {
          _defect_id: defect.id,
          _comment: rejectionReason.trim(),
        },
      );

      if (error) throw error;

      // Fire-and-forget: notify without blocking UI
      const notifyUserId = defect.assigned_to ?? defect.created_by;
      if (notifyUserId) {
        notifyDefectStatusChanged(notifyUserId, defect.title, defect.id, "in_progress").catch(
          (err) => console.error("Failed to send rejection notification", err),
        );
      }

      toast.success(t("defects.verifyDialog.rejectedTitle"), {
        description: t("defects.verifyDialog.rejectedDescription"),
      });

      resetForm();
      onOpenChange(false);
      onVerified?.();
    } catch (error) {
      console.error("Error rejecting defect:", error);
      toast.error(t("common.error"), {
        description: t("defects.verifyDialog.errorDescription"),
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setVerificationNotes("");
    setRejectionReason("");
    setOverrideReason("");
    setActiveTab("verify");
  };

  const handleClose = () => {
    if (!saving) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {t("defects.verifyDialog.title")}
          </DialogTitle>
          <DialogDescription className="min-w-0">
            <TruncatedText text={defect?.title || ""} maxLines={2} showTooltip={false} />
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "verify" | "reject")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="verify" className="gap-2">
              <ShieldCheck className="h-4 w-4" />
              {t("defects.verifyDialog.verifyTab")}
            </TabsTrigger>
            <TabsTrigger value="reject" className="gap-2">
              <ShieldX className="h-4 w-4" />
              {t("defects.verifyDialog.rejectTab")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="verify" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              {t("defects.verifyDialog.verifyDescription")}
            </p>
            {openGateCount > 0 && (
              <div
                className="rounded-md border border-warning/50 bg-warning/10 p-3 space-y-2"
                data-testid="defect-verify-open-actions-warning"
              >
                <p className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  {t("defects.verifyDialog.openActionsWarning", { count: openGateCount })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("defects.verifyDialog.openActionsWarningDesc")}
                </p>
                <div className="space-y-1">
                  <Label htmlFor="override-reason">
                    {t("defects.verifyDialog.overrideReasonLabel")} *
                  </Label>
                  <Textarea
                    id="override-reason"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder={t("defects.verifyDialog.overrideReasonPlaceholder")}
                    className="min-h-[60px]"
                    data-testid="defect-verify-override-reason"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="verification-notes">{t("defects.verifyDialog.notesLabel")} *</Label>
              <Textarea
                id="verification-notes"
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder={t("defects.verifyDialog.notesPlaceholder")}
                className="min-h-[80px]"
              />
            </div>
          </TabsContent>

          <TabsContent value="reject" className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              {t("defects.verifyDialog.rejectDescription")}
            </p>
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">
                {t("defects.verifyDialog.rejectionReasonLabel")} *
              </Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t("defects.verifyDialog.rejectionReasonPlaceholder")}
                className="min-h-[80px]"
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          {activeTab === "verify" ? (
            <Button
              onClick={handleVerify}
              disabled={saving}
              className="gap-2 bg-success hover:bg-success/90"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              <ShieldCheck className="h-4 w-4" />
              {t("defects.verifyDialog.confirmVerify")}
            </Button>
          ) : (
            <Button
              onClick={handleReject}
              disabled={saving}
              variant="destructive"
              className="gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              <ShieldX className="h-4 w-4" />
              {t("defects.verifyDialog.confirmReject")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
